import { defineStore } from 'pinia';
import axios from 'axios';

axios.defaults.withCredentials = true;
const API = import.meta.env.VITE_API || '/api';

const applyCsrfToken = (token?: string) => {
  if (!token) {
    return;
  }

  axios.defaults.headers.common['X-CSRF-TOKEN'] = token;

  const metaTag = document.head.querySelector('meta[name="csrf-token"]');
  if (metaTag) {
    metaTag.setAttribute('content', token);
  }
};

export const useAuth = defineStore('auth', {
  state: () => ({
    user: null as null | { id: number; email: string },
    email: '',
    pinRequested: false,
    isInitialized: false
  }),
  getters: {
    isAuthenticated: (state) => !!state.user
  },
  actions: {
    async requestPin(email: string) {
      this.email = email.trim().toLowerCase();
      await axios.post(`${API}/auth/request-pin`, { email: this.email }, { withCredentials: true });
      this.pinRequested = true;
    },
    async verifyPin(code: string) {
      const { data } = await axios.post(`${API}/auth/verify-pin`, { email: this.email, code }, { withCredentials: true });
      applyCsrfToken(data.csrf_token);
      this.user = data.user;
    },
    async directLogin(email: string, code: string) {
      const normalizedEmail = email.trim().toLowerCase();
      const { data } = await axios.post(`${API}/auth/verify-pin`, { email: normalizedEmail, code }, { withCredentials: true });
      applyCsrfToken(data.csrf_token);
      this.user = data.user;
      this.email = normalizedEmail;
    },
    async checkAuth() {
      try {
        // Try to get current form to check if user is authenticated
        const res = await axios.get(`${API}/form/current`, {
          withCredentials: true,
          // Treat 401/403 as non-exceptional so we avoid noisy console errors on login page
          validateStatus: (s) => s < 400 || s === 401 || s === 403
        });
        if (res.status === 200) {
          const data = res.data;
          // If we get here, user is authenticated
          // Extract user info from the response or set default
          this.user = {
            id: data.user_id || 1,
            email: data.email || this.email || ''
          };
          this.isInitialized = true;
          return true;
        }
        // For 401 or other handled statuses, mark unauthenticated
        this.user = null;
        this.email = '';
        this.pinRequested = false;
        this.isInitialized = true;
        return false;
      } catch (error: any) {
        // User is not authenticated - clear any existing state
        this.user = null;
        this.email = '';
        this.pinRequested = false;
        this.isInitialized = true;
        return false;
      }
    },
    async logout() {
      const { data } = await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
      applyCsrfToken(data?.csrf_token);
      this.user = null;
      this.email = '';
      this.pinRequested = false;
      // Force re-check on next navigation to avoid history-back re-auth
      this.isInitialized = false;
    }
  }
});
